/**
 * The registration manifest — every `defineElement` call AND the
 * HTMLElementTagNameMap augmentation, in ONE file (guide Step 4). Tag names are
 * app-prefixed kebab-case domain nouns.
 */

import { defineBrandMark, defineElement, defineOutlet, defineScopeNav, type KitBrandMark, type KitScopeNav } from "@platform/web-kit"
import { defineRlhfElements } from "@platform/rlhf-elements"
import { CareerNextStep } from "./lib/next-step"
import { CareerApp } from "./surfaces/career-app"
import { CareerLogin } from "./surfaces/career-login"
import { CareerNotFound } from "./surfaces/career-not-found"
import { CareerHeartbeat } from "./surfaces/career-heartbeat"
import { CareerOpportunityDetail } from "./surfaces/career-opportunity-detail"
import { CareerLlmSettings } from "./surfaces/career-llm-settings"
import { CareerThemeSettings } from "./surfaces/career-theme-settings"
import { CareerLlmReview } from "./surfaces/career-llm-review"
import { CareerOnboarding } from "./surfaces/career-onboarding"
import { CareerExtractionReview } from "./surfaces/career-extraction-review"
import { CareerCvProposal } from "./surfaces/career-cv-proposal"
import { CareerWerteView } from "./surfaces/career-werte-view"
import { CareerProfile } from "./surfaces/career-profile"
import { CareerCvWorkbench } from "./surfaces/career-cv-workbench"
import { CareerEvidenceLedger } from "./surfaces/career-evidence-ledger"
import { CareerAssets } from "./surfaces/career-assets"
import { CareerCvPrint } from "./surfaces/career-cv-print"
import { CareerWorkshopList } from "./surfaces/career-workshop-list"
import { CareerWorkshopRunner } from "./surfaces/career-workshop-runner"
import { CareerValuesCardgame } from "./surfaces/career-values-cardgame"
import { CareerValuesInterview } from "./surfaces/career-values-interview"
import { CareerWerteSektion } from "./surfaces/career-werte-sektion"
import { CareerWritingStyle } from "./surfaces/career-writing-style"
import { CareerSearchProfile } from "./surfaces/career-search-profile"
import { CareerDecisionProfile } from "./surfaces/career-decision-profile"
import { CareerSearch } from "./surfaces/career-search"
import { CareerApplications } from "./surfaces/career-applications"
import { CareerApplicationReview } from "./surfaces/career-application-review"
import { CareerRuns } from "./surfaces/career-runs"
import { CareerProcesses } from "./surfaces/career-processes"
import { CareerCredits } from "./surfaces/career-credits"
import { CareerBenchGrading } from "./surfaces/career-bench-grading"
import { CareerBenchLab } from "./surfaces/career-bench-lab"

export function registerElements(): void {
  defineOutlet() // once, before router.start()
  defineBrandMark() // <kit-brand-mark> (platform P-mark) + its stylesheet — login branding
  defineScopeNav() // <kit-scope-nav> (the platform scope navigation) — the shell's top bar
  defineRlhfElements() // <rlhf-*> grading elements (dev bench-grading surface) + their styles
  // The ONE golden-path forward affordance (D4/R5) — a shared LEAF, not a routed surface;
  // `nextStep()` in app/lib/next-step.ts is the adapter every workflow surface emits it through.
  defineElement("career-next-step", CareerNextStep)
  defineElement("career-app", CareerApp)
  defineElement("career-login", CareerLogin)
  defineElement("career-not-found", CareerNotFound)
  defineElement("career-heartbeat", CareerHeartbeat)
  defineElement("career-opportunity-detail", CareerOpportunityDetail)
  defineElement("career-onboarding", CareerOnboarding)
  defineElement("career-extraction-review", CareerExtractionReview)
  defineElement("career-cv-proposal", CareerCvProposal)
  defineElement("career-werte-view", CareerWerteView)
  defineElement("career-profile", CareerProfile)
  defineElement("career-cv-workbench", CareerCvWorkbench)
  defineElement("career-evidence-ledger", CareerEvidenceLedger)
  defineElement("career-assets", CareerAssets)
  defineElement("career-cv-print", CareerCvPrint)
  defineElement("career-workshop-list", CareerWorkshopList)
  defineElement("career-workshop-runner", CareerWorkshopRunner)
  defineElement("career-values-cardgame", CareerValuesCardgame)
  defineElement("career-values-interview", CareerValuesInterview)
  defineElement("career-werte-sektion", CareerWerteSektion)
  defineElement("career-writing-style", CareerWritingStyle)
  defineElement("career-search-profile", CareerSearchProfile)
  defineElement("career-decision-profile", CareerDecisionProfile)
  defineElement("career-search", CareerSearch)
  defineElement("career-applications", CareerApplications)
  defineElement("career-application-review", CareerApplicationReview)
  defineElement("career-runs", CareerRuns)
  // Admin-Konsole: the authored process graphs. Routed for everyone; the api 403s the read.
  defineElement("career-processes", CareerProcesses)
  defineElement("career-credits", CareerCredits)
  defineElement("career-llm-settings", CareerLlmSettings)
  defineElement("career-theme-settings", CareerThemeSettings) // dev-gated design/theme tuning
  // Global, not routed: the review pane lives in the shell and renders nothing unless a call of
  // the caller's own is paused for review (§9.2).
  defineElement("career-llm-review", CareerLlmReview)
  defineElement("career-bench-grading", CareerBenchGrading)
  defineElement("career-bench-lab", CareerBenchLab)
}

declare global {
  interface HTMLElementTagNameMap {
    "kit-brand-mark": KitBrandMark
    "kit-scope-nav": KitScopeNav
    "career-next-step": CareerNextStep
    "career-app": CareerApp
    "career-login": CareerLogin
    "career-not-found": CareerNotFound
    "career-heartbeat": CareerHeartbeat
    "career-opportunity-detail": CareerOpportunityDetail
    "career-onboarding": CareerOnboarding
    "career-extraction-review": CareerExtractionReview
    "career-cv-proposal": CareerCvProposal
    "career-werte-view": CareerWerteView
    "career-profile": CareerProfile
    "career-cv-workbench": CareerCvWorkbench
    "career-evidence-ledger": CareerEvidenceLedger
    "career-assets": CareerAssets
    "career-cv-print": CareerCvPrint
    "career-workshop-list": CareerWorkshopList
    "career-workshop-runner": CareerWorkshopRunner
    "career-values-cardgame": CareerValuesCardgame
    "career-werte-sektion": CareerWerteSektion
    "career-writing-style": CareerWritingStyle
    "career-search-profile": CareerSearchProfile
    "career-decision-profile": CareerDecisionProfile
    "career-search": CareerSearch
    "career-applications": CareerApplications
    "career-application-review": CareerApplicationReview
    "career-runs": CareerRuns
    "career-processes": CareerProcesses
    "career-credits": CareerCredits
    "career-llm-settings": CareerLlmSettings
    "career-theme-settings": CareerThemeSettings
    "career-llm-review": CareerLlmReview
    "career-bench-grading": CareerBenchGrading
    "career-bench-lab": CareerBenchLab
  }
}
